
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import PdfAPI from '@/api/PdfAPI';
import { MeasurementPlanHistoryEntry } from '@/interfaces/components/MeasurementPlanHistoryEntry';
import { downloadTableAsExcel, ExportColumn } from '@/helpers/PrintHelper';
import moment from 'moment';
// @ts-ignore
import downloadFile from 'downloadjs';

@Component({ name: 'MeasurementPlanHistoryModal' })
export default class MeasurementPlanHistoryModal extends Vue {
  @Prop({ required: true }) private visible!: boolean;

  private entries: MeasurementPlanHistoryEntry[] = [];
  private isLoading = false;
  private downloadingId: number | null = null;
  private printingId: number | null = null;
  private search = '';

  private readonly queryParamName = 'printHistory';

  private get columns() {
    return [
      {
        title: this.$t('User'),
        dataIndex: 'userName',
        scopedSlots: { customRender: 'userSlot' },
        sorter: (a: any, b: any) => (a.userName || '').localeCompare(b.userName || ''),
        sortDirections: ['descend', 'ascend'],
        width: '25%',
      },
      {
        title: this.$t('Commercialist'),
        dataIndex: 'commercialistName',
        scopedSlots: { customRender: 'commercialistSlot' },
        sorter: (a: any, b: any) => (a.commercialistName || '').localeCompare(b.commercialistName || ''),
        sortDirections: ['descend', 'ascend'],
        width: '25%',
      },
      {
        title: this.$t('Printed at'),
        dataIndex: 'printedAt',
        scopedSlots: { customRender: 'printedAtSlot' },
        sorter: (a: any, b: any) => moment(a.printedAt).valueOf() - moment(b.printedAt).valueOf(),
        sortDirections: ['descend', 'ascend'],
        defaultSortOrder: 'descend',
        width: '25%',
      },
      {
        title: this.$t('Actions'),
        dataIndex: 'id',
        scopedSlots: { customRender: 'actionsSlot' },
        width: '25%',
        align: 'center',
      },
    ];
  }

  private get filteredList(): any[] {
    if (!this.entries || !this.entries.length) {
      return [];
    }
    const query = this.search.toLowerCase();
    return this.entries
      .filter((entry: any) =>
        (entry.user?.name || '').toLowerCase().includes(query) ||
        (entry.commercialist?.name || '').toLowerCase().includes(query) ||
        (entry.filename || '').toLowerCase().includes(query),
      )
      .map((entry: any) => ({
        ...entry,
        key: entry.id,
        userName: entry.user?.name || '',
        commercialistName: entry.commercialist?.name || '',
      }));
  }

  private get exportList(): any[] {
    return this.filteredList.map((row: any) => ({
      user: row.userName || '—',
      commercialist: row.commercialistName || '—',
      printedAt: this.formatDate(row.printedAt),
    }));
  }

  @Watch('visible')
  private async onVisibleChange(val: boolean) {
    this.syncQueryParam(val);
    if (val) {
      await this.fetchHistory();
    }
  }

  private mounted() {
    if (this.$route.query[this.queryParamName] !== undefined && !this.visible) {
      this.$emit('open');
    }
  }

  private syncQueryParam(isOpen: boolean) {
    const currentQuery = { ...this.$route.query };
    if (isOpen) {
      if (currentQuery[this.queryParamName] === undefined) {
        currentQuery[this.queryParamName] = '1';
        this.$router.replace({ query: currentQuery }).catch(() => {});
      }
    } else if (currentQuery[this.queryParamName] !== undefined) {
      delete currentQuery[this.queryParamName];
      this.$router.replace({ query: currentQuery }).catch(() => {});
    }
  }

  public async refresh() {
    await this.fetchHistory();
  }

  private async fetchHistory() {
    this.isLoading = true;
    try {
      this.entries = await PdfAPI.getMeasurementPlanHistory();
    } catch {
      this.$notification.error({
        message: this.$t('Error') as string,
        description: this.$t('Could not load print history.') as string,
      });
    } finally {
      this.isLoading = false;
    }
  }

  private async handleRedownload(entry: MeasurementPlanHistoryEntry) {
    this.downloadingId = entry.id;
    try {
      const blob = await PdfAPI.redownloadMeasurementPlanPDF(entry.id);
      const formattedDate = moment(entry.printedAt).format('DD-MM-YYYY');
      downloadFile(blob, `Measurement_Plan_${formattedDate}.pdf`, 'application/pdf');
      this.$notification.success({
        message: this.$t('Success') as string,
        description: this.$t('PDF downloaded successfully.') as string,
      });
    } catch {
      this.$notification.error({
        message: this.$t('Error') as string,
        description: this.$t('Could not re-download this PDF.') as string,
      });
    } finally {
      this.downloadingId = null;
    }
  }

  private async handlePrint(entry: MeasurementPlanHistoryEntry) {
    this.printingId = entry.id;
    try {
      const blob = await PdfAPI.redownloadMeasurementPlanPDF(entry.id);
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch {
      this.$notification.error({
        message: this.$t('Error') as string,
        description: this.$t('Could not print this PDF.') as string,
      });
    } finally {
      this.printingId = null;
    }
  }

  private downloadExcel() {
    const tableData = this.exportList;
    if (!tableData || tableData.length === 0) {
      return;
    }
    const exportColumns = [
      { title: this.$t('User'), dataIndex: 'user' },
      { title: this.$t('Commercialist'), dataIndex: 'commercialist' },
      { title: this.$t('Printed at'), dataIndex: 'printedAt' },
    ] as ExportColumn[];
    downloadTableAsExcel(exportColumns, tableData, 'Measurement_Plan_History');
  }

  private formatDate(iso: string): string {
    return moment(iso).format('DD.MM.YYYY HH:mm');
  }
}
